import React from 'react';
import { FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { resolveLabelSrc, openLabelInNewTab, printLabel, downloadLabel } from '../../utils/labelUtils';

const AdminLabelsTab = ({ bookings, bookingsWithLabels, setActiveTab }) => {
  const totalWithLabels = bookings.filter(b => b.packages?.some(p => p.label_url)).length;

  return (
    <div data-testid="admin-labels-tab">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '700' }}>All Labels & QR Codes</h2>
        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
          {totalWithLabels} bookings with uploaded files
        </div>
      </div>

      {/* Filter buttons */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <Button
          size="sm"
          onClick={() => setActiveTab('labels')}
          style={{
            padding: '0.5rem 1rem',
            fontSize: '0.875rem',
            backgroundColor: '#10b981',
            color: 'white',
          }}
        >
          All Files
        </Button>
      </div>

      {/* Labels Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1.5rem' }}>
        {bookingsWithLabels
          .map(booking =>
            booking.packages
              .filter(pkg => pkg.label_url)
              .map((pkg, pkgIdx) => (
                <Card key={`${booking.id}-${pkgIdx}`} style={{ border: '2px solid #10b981' }}>
                  <CardHeader style={{ backgroundColor: '#f0fdf4', padding: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                          Confirmation #{booking.confirmation_number}
                        </p>
                        <p style={{ fontSize: '1rem', fontWeight: '700', color: '#065f46', marginBottom: '0.25rem' }}>
                          Package {pkgIdx + 1} of {booking.packages.length}
                        </p>
                        {pkg.tracking_last6 && (
                          <p style={{ fontSize: '0.875rem', color: '#10b981', fontWeight: '600' }}>
                            Tracking: ...{pkg.tracking_last6}
                          </p>
                        )}
                      </div>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '700',
                        backgroundColor: pkg.package_type === 'qr' ? '#dbeafe' : '#fef3c7',
                        color: pkg.package_type === 'qr' ? '#1e40af' : '#92400e',
                      }}>
                        {pkg.package_type === 'qr' ? '📱 QR CODE' : '🏷️ LABEL'}
                      </span>
                    </div>
                    <p style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '0.25rem' }}>
                      <strong>Carrier:</strong> {pkg.carrier}
                    </p>
                    <p style={{ fontSize: '0.875rem', color: '#374151' }}>
                      <strong>Customer:</strong> {booking.customer_name || booking.customer_email}
                    </p>
                  </CardHeader>
                  <CardContent style={{ padding: '1rem' }}>
                    {/* Label/QR Preview */}
                    <div style={{
                      backgroundColor: 'white',
                      padding: '0.5rem',
                      borderRadius: '6px',
                      border: '2px solid #d1d5db',
                      marginBottom: '1rem',
                      textAlign: 'center',
                    }}>
                      {pkg.label_url || pkg.qr_image ? (
                        <img
                          src={resolveLabelSrc(pkg)}
                          alt={pkg.package_type === 'qr' ? 'QR Code' : 'Return Label'}
                          style={{
                            maxWidth: '100%',
                            maxHeight: pkg.package_type === 'qr' ? '200px' : '300px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                          }}
                          onClick={() => openLabelInNewTab(resolveLabelSrc(pkg))}
                        />
                      ) : (
                        <div style={{ padding: '2rem', color: '#9ca3af', fontSize: '0.875rem' }}>
                          No label/QR code uploaded
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    {(pkg.label_url || pkg.qr_image) && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                        <Button
                          size="sm"
                          onClick={() => {
                            const src = resolveLabelSrc(pkg);
                            if (pkg.package_type === 'qr') {
                              openLabelInNewTab(src);
                            } else {
                              printLabel(
                                src,
                                `${booking.confirmation_number}-pkg${pkgIdx + 1}-label`,
                              );
                            }
                          }}
                          style={{
                            padding: '0.75rem',
                            fontSize: '0.875rem',
                            backgroundColor: '#10b981',
                            color: 'white',
                            fontWeight: '600',
                          }}
                          data-testid={`labels-print-btn-${pkgIdx}`}
                        >
                          {pkg.package_type === 'qr' ? '📱 View QR' : '🖨️ Print'}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            const src = resolveLabelSrc(pkg);
                            downloadLabel(
                              src,
                              `${booking.confirmation_number}-pkg${pkgIdx + 1}-${pkg.package_type}.png`,
                            );
                          }}
                          style={{
                            padding: '0.75rem',
                            fontSize: '0.875rem',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            fontWeight: '600',
                          }}
                          data-testid={`labels-download-btn-${pkgIdx}`}
                        >
                          💾 Download
                        </Button>
                      </div>
                    )}

                    {/* Package Info */}
                    <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.75rem' }}>
                        {pkg.packaging_size && (
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#f3e8ff',
                            color: '#7c3aed',
                            borderRadius: '4px',
                            fontWeight: '600',
                          }}>
                            📦 {pkg.packaging_size}
                          </span>
                        )}
                        {pkg.rush_delivery && (
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#fee2e2',
                            color: '#dc2626',
                            borderRadius: '4px',
                            fontWeight: '600',
                          }}>
                            ⚡ RUSH
                          </span>
                        )}
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: pkg.status === 'delivered' ? '#d1fae5' : '#fef3c7',
                          color: pkg.status === 'delivered' ? '#065f46' : '#92400e',
                          borderRadius: '4px',
                          fontWeight: '600',
                        }}>
                          {pkg.status || 'pending'}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )),
          )}
      </div>

      {/* Empty state */}
      {totalWithLabels === 0 && (
        <Card style={{ padding: '3rem', textAlign: 'center' }}>
          <FileText size={64} color="#9ca3af" style={{ margin: '0 auto 1rem' }} />
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
            No Labels or QR Codes Yet
          </h3>
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
            Labels and QR codes will appear here when customers upload them during booking.
          </p>
        </Card>
      )}
    </div>
  );
};

export default AdminLabelsTab;
